import { ILandingContract } from "@features/landing/model";
import { getDeviceId, landingBeginClicked } from "@features/shared/events";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { createEffect, createEvent, createStore, sample } from "effector";
import { delay, interval } from "patronum";

const calculateDailyMinutes = (targetIncome: number, selectedAreas: string[]) => {
    const baseMinutes = (targetIncome / 1000) * 5;
    const professionalAreaMultiplier = 1 + (selectedAreas.length - 1) * 0.2;
    const totalMinutes = Math.round(baseMinutes * professionalAreaMultiplier);
    return Math.min(totalMinutes, 30);
};

const signupButtonClicked = createEvent<string>();
const toggleProfessionalArea = createEvent<string>();
const changeMoneyCount = createEvent<number>();
const isCalculatorVisible = createEvent();
const startCounter = createEvent();
const stopCounter = createEvent();
const delayed = createEvent();

const { tick } = interval({
    timeout: 15,
    start: startCounter,
    stop: stopCounter
});

const $professionalAreas = createStore<string[]>([]);
const $selectedProfessionalAreas = createStore<string[]>([]);
const $moneyCount = createStore(0)
    .on(changeMoneyCount, (_, p) => p)
    .on(tick, (s) => s + 100);
const $minutesPerDay = createStore(0);

const trackLandingBeginClickedFx = createEffect((source: string) => {
    landingBeginClicked(source);
});

const hardNavigateToExperimentLandingFx = createEffect((url: string) => {
    if (typeof window !== "undefined") {
        window.location.assign(url);
    }
});

sample({
    clock: signupButtonClicked,
    fn: () => {
        const useTestFunnel = localStorage.getItem("useTestFunnel") === "true"
        const baseUrl = useTestFunnel
            ? "https://testingservices.xyz/api/experiment/landing"
            : "https://tixu.ai/api/experiment/landing"

        if (typeof window === "undefined") {
            return baseUrl;
        }

        const currentUrl = new URL(window.location.href);
        const query = currentUrl.search;

        const deviceId = getDeviceId();

        const targetUrl = new URL(baseUrl + query);
        if (deviceId) {
            targetUrl.searchParams.set("deviceId", deviceId);
        }

        return targetUrl.toString();
    },
    target: hardNavigateToExperimentLandingFx
});

sample({
    clock: signupButtonClicked,
    target: trackLandingBeginClickedFx
});

sample({
    clock: toggleProfessionalArea,
    source: $selectedProfessionalAreas,
    fn: (selected, toggled) =>
        selected.includes(toggled) ? selected.filter((p) => p !== toggled) : [...selected, toggled],
    target: $selectedProfessionalAreas
});

sample({
    clock: [$moneyCount, $selectedProfessionalAreas],
    source: { $moneyCount, $selectedProfessionalAreas },
    fn: ({ $moneyCount, $selectedProfessionalAreas }) => calculateDailyMinutes($moneyCount, $selectedProfessionalAreas),
    target: $minutesPerDay
});

sample({
    clock: isCalculatorVisible,
    target: startCounter
});

delay({
    source: isCalculatorVisible,
    timeout: 500,
    target: delayed
});

sample({
    clock: delayed,
    source: { $professionalAreas },
    fn: ({ $professionalAreas }) => $professionalAreas[0],
    target: toggleProfessionalArea
});

sample({
    clock: $moneyCount,
    filter: (count) => count >= 3000,
    target: stopCounter
});

const contract = new ModelContract(
    {
        $professionalAreas,
        $selectedProfessionalAreas,
        $moneyCount,
        $minutesPerDay
    },
    {
        signupButtonClicked,
        toggleProfessionalArea,
        changeMoneyCount,
        isCalculatorVisible
    },
    {},
    {}
);

export type IProfessionFormContract = typeof contract;

export class ProfessionFormModule extends ModuleDescription<IProfessionFormContract> {
    constructor(public landingModule: ILandingContract) {
        super();
    }
    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: this.landingModule.stores.$professionsData,
            fn: (professions: string[]) => professions,
            target: $professionalAreas
        });
    }
}
